"use strict";
import { Store } from '@ngrx/store';
import { select } from '@shared/datastore/operators/select';
import { Observable } from 'rxjs/Observable';
import { combineLatest, distinctUntilChanged, filter, map, pairwise, pluck, shareReplay, startWith, switchMap, take, tap, } from 'rxjs/operators';
import { Auth } from '../auth/auth.service';
import { ErrorTracking } from '../tracking';
import { ApiHttp } from './api-http.service';
import { arrayIsShallowEqual, isDefined, objectFilterByKeys, referenceToReferenceList, sameElements, setDiff, stringifyQuery, toObservable, } from './helpers';
import { ListObservable } from './list-observable';
import { ObjectObservable } from './object-observable';
import { WebSocketService } from './websocket';
var Datastore = /** @class */ (function () {
    function Datastore(store, apiHttp, auth, webSocketService, errorTracking) {
        this.store = store;
        this.apiHttp = apiHttp;
        this.auth = auth;
        this.webSocketService = webSocketService;
        this.errorTracking = errorTracking;
    }
    Datastore.prototype.list = function (key1, key2, options) {
        var _this = this;
        var optionsQuery = options && options.query ? options.query : {};
        var refStream = Observable.combineLatest([
            toObservable(key1),
            toObservable(key2),
            toObservable(optionsQuery.limit),
            toObservable(optionsQuery.offset),
            toObservable(optionsQuery.params),
        ], function (path1, path2, limit, offset, params) {
            var path = [path1, path2];
            return { path: path, query: { limit: limit, offset: offset, params: params } };
        }).pipe(tap(function (ref) {
            var action = {
                type: 'REQUEST_DATA',
                payload: {
                    type: ref.path[0],
                    refList: referenceToReferenceList(ref),
                },
            };
            _this.store.dispatch(action);
        }), shareReplay(1));
        return new ListObservable(refStream, this.apiHttp, this.auth, this.store, this.errorTracking, refStream.pipe(switchMap(function (_a) {
            var _b = _a.path, path1 = _b[0], path2 = _b[1], query = _a.query;
            return _this.store.pipe(select(path1, path2), map(function (data) {
                var queryString = stringifyQuery(query);
                if (data && data.lists && data.lists[queryString]) {
                    return ((data.lists[queryString] || { ids: [] }).ids || [])
                        .slice((query && query.offset) || 0)
                        .map(function (id) { return data.entities[id].entity; });
                }
                return [];
            }), distinctUntilChanged(arrayIsShallowEqual));
        })));
    };
    Datastore.prototype.object = function (key1, key2) {
        var _this = this;
        var refStream = Observable.combineLatest([toObservable(key1), toObservable(key2)], function (path1, path2) {
            var path = [path1, path2];
            return { path: path };
        }).pipe(switchMap(function (_a) {
            var path = _a.path;
            return _this.store.pipe(pluck.apply(void 0, path), take(1));
        }, function (ref, data) { return ({ ref: ref, data: data }); }), tap(function (_a) {
            var ref = _a.ref, data = _a.data;
            // Don't fetch if this was fetched in the last second.
            if (!data || Number(data.timeFetched) + 1000 < Date.now()) {
                var action = {
                    type: 'REQUEST_DATA',
                    payload: {
                        type: ref.path[0],
                        refList: referenceToReferenceList(ref),
                    },
                };
                _this.store.dispatch(action);
            }
        }), map(function (_a) {
            var ref = _a.ref;
            return ref;
        }), shareReplay(1));
        var subscriptionStream = key1 === 'onlineOffline'
            ? this.webSocketService
                .subscribeOnlineOffline(refStream.pipe(map(function (ref) { return parseInt(ref.path[1], 10); })))
                .pipe(combineLatest(refStream, function (ss, ref) { return ref; }))
            : refStream;
        return new ObjectObservable(refStream, this.apiHttp, this.store, Observable.merge(refStream, subscriptionStream).pipe(switchMap(function (_a) {
            var _b = _a.path, path1 = _b[0], path2 = _b[1];
            return _this.store.pipe(select(path1, path2), filter(isDefined), map(function (obj) { return obj.entity; }), distinctUntilChanged());
        })));
    };
    /* Returns a list of objects at a location.
     * If an observable is passed in for key2 then only the delta will be
     * refetched from the network.
     */
    Datastore.prototype.objects = function (key1, key2) {
        var _this = this;
        var args$ = [
            key1,
            key2,
        ].map(function (key) { return (key instanceof Observable ? key : Observable.of(key)); });
        var refStream = Observable.combineLatest(args$).pipe(map(function (pathList) { return ({ refList: { pathList: pathList } }); }), startWith({ refList: { pathList: [key1, []] } }), pairwise(), // Use pairwise and only fetch new things
        tap(function (_a) {
            var refList1 = _a[0].refList, refList2 = _a[1].refList;
            var newIds = setDiff(refList2.pathList[1], refList1.pathList[1]);
            if (newIds.length) {
                var type = refList2.pathList[0];
                var action = {
                    type: 'REQUEST_DATA',
                    payload: { type: type, refList: { pathList: [type, newIds] } },
                };
                _this.store.dispatch(action);
            }
        }), map(function (_a) {
            var refList1 = _a[0].refList, refList2 = _a[1].refList;
            return refList2;
        }), shareReplay(1));
        return refStream.pipe(switchMap(function (_a) {
            var pathList = _a.pathList;
            return _this.store.pipe(select(pathList[0]), filter(isDefined), map(function (rawEntitiesObject) {
                return objectFilterByKeys(rawEntitiesObject, pathList[1]);
            }), map(function (rawEntitiesObject) { return Object.values(rawEntitiesObject); }), map(function (rawEntities) { return rawEntities.map(function (rawEntity) { return rawEntity.entity; }); }), distinctUntilChanged(sameElements));
        }));
    };
    return Datastore;
}());
export { Datastore };



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-webapp-compat/webapp/src/app/@shared/datastore/datastore.ts.pre-build-optimizer.js